import { type MouseEvent, memo } from 'react';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import {
  workspaceAgentComposerPillRemoveButton,
  workspaceAgentComposerScopeBar,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { WorkspaceAgentComposerScopeBarKindIcon } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/composer/WorkspaceAgentComposerScopeBarKindIcon';
import { type WorkspaceAgentComposerScopeKind } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentComposerScopeChip';

export type WorkspaceAgentComposerScopeBarProps = {
  kind: WorkspaceAgentComposerScopeKind;
  name: string;
  onRemove?: () => void;
  title: string;
};

const handleRemoveMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
};

export const WorkspaceAgentComposerScopeBar = memo(
  ({ kind, name, onRemove, title }: WorkspaceAgentComposerScopeBarProps) => {
    const removeLabel = (() => {
      if (kind === 'board') {
        return 'Remove current board from scope';
      }

      if (kind === 'asset-stack') {
        return 'Remove all selected assets';
      }

      return 'Remove selected asset';
    })();
    const showFullTitleForAssistiveTech = title !== name;

    return (
      <div className={workspaceAgentComposerScopeBar()} data-testid="WORKSPACE_AGENT_COMPOSER_SCOPE_BAR">
        <span className="flex size-4 shrink-0 items-center justify-center ads:text-icon-default text-gray-10">
          <WorkspaceAgentComposerScopeBarKindIcon kind={kind} />
        </span>
        <Tooltip label={title}>
          <span className="min-w-0 flex-1 truncate font-medium ads:text-primary text-12 text-gray-12 leading-4">
            {showFullTitleForAssistiveTech ? (
              <>
                <span aria-hidden="true">{name}</span>
                <span className="sr-only">{title}</span>
              </>
            ) : (
              name
            )}
          </span>
        </Tooltip>
        {onRemove ? (
          <IconButton
            adsIcon
            appearance="ghost"
            className={workspaceAgentComposerPillRemoveButton()}
            color="gray"
            data-testid="WORKSPACE_AGENT_COMPOSER_SCOPE_BAR_REMOVE"
            label={removeLabel}
            onClick={onRemove}
            onMouseDown={handleRemoveMouseDown}
            size="extra-small"
            icon={XIcon}
          />
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentComposerScopeBar.displayName = 'WorkspaceAgentComposerScopeBar';
